import { ChangeDetectionStrategy, Component, inject, Input, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Patient } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { PatientHttpService } from '@services/http/patient.http.service';
import { PatientTeleexpertiseStep } from '@shared/models/enums/patient-teleexpertise-step.enum';
import { TeleexpertiseProvider } from '@shared/models/enums/teleexpertise-provider.enum';
import { TeleexpertiseProviderPipe } from '@shared/pipes/teleexpertise-provider.pipe';
import { TuiAnimated } from '@taiga-ui/cdk';
import { TuiButton, TuiDialogContext, TuiInput, TuiLabel, TuiLoader, TuiNotificationService } from '@taiga-ui/core';
import { TuiNotificationMiddle, TuiStepper, TuiTextarea } from '@taiga-ui/kit';
import { TuiFloatingContainer, TuiSlides } from '@taiga-ui/layout';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { first, switchMap, tap } from 'rxjs';
import { PatientCardComponent } from '../patient-card/patient-card.component';
import { PatientReportDocumentComponent } from '@modules/patient/patient-report-document/patient-report-document.component';
import { PatientReportDocumentService } from '@modules/patient/patient-report-document/patient-report-document.service';
import { CopyToClipboardDirective } from '@directives/copy-to-clipboard.directive';
import { SlidesScrollResetDirective } from '@modules/shared/directives/slides-scroll-reset.directive';

@Component({
  selector: 'pxc-patient-teleexpertise-request',
  imports: [
    SlidesScrollResetDirective,
    TuiStepper,
    TuiButton,
    PatientCardComponent,
    TuiLabel,
    TuiInput,
    TuiTextarea,
    TuiFloatingContainer,
    TuiSlides,
    FormsModule,
    ReactiveFormsModule,
    TuiAnimated,
    TuiLoader,
    TuiNotificationMiddle,
    TranslocoDirective,
    TeleexpertiseProviderPipe,
    PatientReportDocumentComponent,
    CopyToClipboardDirective,
  ],
  providers: [TeleexpertiseProviderPipe, PatientReportDocumentService],
  host: { class: 'flex flex-1 flex-col gap-8' },
  templateUrl: './patient-teleexpertise-request.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientTeleexpertiseRequestComponent {
  private readonly context =
    inject<TuiDialogContext<any, Partial<PatientTeleexpertiseRequestComponent>>>(POLYMORPHEUS_CONTEXT);
  private readonly patientHttpService = inject(PatientHttpService);
  private readonly alertService = inject(TuiNotificationService);
  private readonly translocoService = inject(TranslocoService);
  private readonly teleexpertiseProviderPipe = inject(TeleexpertiseProviderPipe);
  protected readonly patientReportDocumentService = inject(PatientReportDocumentService);

  @Input() patient: Patient = this.context.data.patient;
  @Input() sequences: FlattenedSequence[] = this.context.data.sequences || [];
  @Input() clientCode: string = this.context.data.clientCode;
  @Input() teleexpertiseProviderType: TeleexpertiseProvider = this.context.data.teleexpertiseProviderType;

  departmentId: number = this.context.data.departmentId;
  message: string = null;
  readonly isSubmitting = signal(false);

  slideDirection = 0;
  private currentStep: PatientTeleexpertiseStep = PatientTeleexpertiseStep.INFORMATIONS;

  get step(): PatientTeleexpertiseStep {
    return this.currentStep;
  }

  set step(step: PatientTeleexpertiseStep) {
    this.slideDirection = step - this.currentStep;
    this.currentStep = step;
  }
  redirectUrl: string | null = null;

  readonly PatientTeleexpertiseStep = PatientTeleexpertiseStep;

  onStepChange(index: number): void {
    if (index === PatientTeleexpertiseStep.RESULT && this.step !== PatientTeleexpertiseStep.RESULT) {
      return;
    }
    this.step = index;
  }

  next(): void {
    if (this.step === PatientTeleexpertiseStep.INFORMATIONS) {
      this.step = PatientTeleexpertiseStep.REPORT;
      return;
    }

    if (this.step === PatientTeleexpertiseStep.RESULT) {
      this.context.completeWith(true);
      return;
    }

    if (this.patientReportDocumentService.form.invalid) {
      return;
    }

    this.patientReportDocumentService.patientReportRequest$
      .pipe(
        first(),
        tap(() => {
          this.isSubmitting.set(true);
        }),
        switchMap((formValues) => {
          return this.patientHttpService.teleexpertise(
            this.clientCode,
            this.patient.id,
            this.message,
            this.teleexpertiseProviderType,
            {
              templateId: formValues.templateId,
              departmentIds: formValues.departmentIds,
              sequenceIds: formValues.sequenceIds || [],
            },
          );
        }),
      )
      .subscribe({
        next: (redirectUrl) => {
          this.redirectUrl = redirectUrl;
          this.step = PatientTeleexpertiseStep.RESULT;
          this.isSubmitting.set(false);
        },
        error: (error) => {
          console.error('patient-teleexpertise-request error:', error);
          this.isSubmitting.set(false);
          this.alertService
            .open(
              this.translocoService.translate('Echec de la téléexpertise avec {{ provider }}', {
                provider: this.teleexpertiseProviderPipe.transform(this.teleexpertiseProviderType),
              }),
              {
                label: this.translocoService.translate('Une erreur est survenue'),
                appearance: 'negative',
              },
            )
            .subscribe();
        },
      });
  }

  previous(): void {
    if (this.step === PatientTeleexpertiseStep.REPORT) {
      this.step = PatientTeleexpertiseStep.INFORMATIONS;
      return;
    }
    this.context.completeWith(this.step === PatientTeleexpertiseStep.RESULT ? true : null);
  }

  openLink(): void {
    if (!this.redirectUrl) {
      return;
    }
    window.open(this.redirectUrl, '_blank');
  }
}
